/* eslint-disable eqeqeq */
import { OverlayTrigger } from "react-bootstrap";
import i18n from "../../i18n";
import { ReadFriendlyNumber } from "../../lib/utils";
import StyledAccordion from "../Accordion/StyledAccordion";
import {
  KickbackChanged,
  KickbackChangeState,
  ProjectReport,
} from "../modal/SubmitKickbackProject";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamation,
  faPen,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import React, { useState } from "react";

type Test = Pick<KickbackChanged, "changesComment" | "changedSum">;
type BLa = Pick<ProjectReport, "commentRowChange" | "sumExclMomsChange">;

type CheckBox = {
  [key: string]: boolean;
};

interface Props {
  sectionRef: React.RefObject<HTMLDivElement>;
  kickbackChangesReport: KickbackChangeState;
  renderKickbackChangesField: (
    item: KickbackChanged,
    keyForProp: keyof Test
  ) => JSX.Element;
  contractConfirmation: CheckBox;
  checkBoxMenu: (contractId: string) => JSX.Element;
  reports: any;
  loading: boolean;
  chageForChangeChange: (
    event: any,
    item: ProjectReport,
    propKey: keyof BLa
  ) => void;
  rowEdit?: boolean;
  showValidationInfo: (propsa: any, key: string) => JSX.Element;
}

const formatNumber = (value: number) => {
  if (isNaN(value)) return "0,00";
  return value.toLocaleString("sv-SE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getRowDeduction = (item: ProjectReport) => {
  return item.sumExclMoms - item.sumExclMomsChange;
};

const getRowEditKey = (item: ProjectReport) => `${item.contractId}-${item.id}`;

export const SupplierChanges = (props: Props): JSX.Element => {
  const [editingRows, setEditingRows] = useState<{ [key: string]: boolean }>({});
  const [projectNameSortDirectionByContract, setProjectNameSortDirectionByContract] =
  useState<Record<string, "asc" | "desc">>({});

const toggleProjectNameSortDirection = (contractId: string) => {
  setProjectNameSortDirectionByContract((prev) => ({
    ...prev,
    [contractId]: prev[contractId] === "asc" ? "desc" : "asc",
  }));
};
  const toggleRowEdit = (item: ProjectReport) => {
    const key = getRowEditKey(item);
    setEditingRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!props.rowEdit) {
    return compView(props);
  } else {
    return renderMultipleChangesRow(props, editingRows, toggleRowEdit,projectNameSortDirectionByContract,toggleProjectNameSortDirection);
  }
};

const renderEditableCommentCell = (
  item: ProjectReport,
  props: Props
): JSX.Element => {
  const deduction = getRowDeduction(item);
  const hasChange = deduction !== 0;
  const showError = hasChange && item.commentRowChange.length == 0;

  return (
    <div style={{ width: "100%", position: "relative" }}>
      <div className={showError ? "" : ""}>
        <input
          spellCheck={false}
          type="text"
          value={item.commentRowChange}
          disabled={!hasChange}
          placeholder={hasChange ? "Obligatorisk anledning..." : ""}
          onChange={(event) =>
            props.chageForChangeChange(event, item, "commentRowChange")
          }
          style={{
            width: "100%",
            minHeight: 36,
            borderRadius: 10,
            border: showError ? "1px solid #dc3545" : "1px solid #ced4da",
            background: !hasChange
              ? '#fffaf5'
              : showError
              ? "rgba(179, 4, 4, 0.04)"
              : "#fffaf5",
            outline: "none",
            padding: "8px 36px 8px 12px",
            fontSize: 14,
            color: !hasChange ? "#94a3b8" : "#334155",
            transition: "all 0.2s ease",
            cursor: !hasChange ? "not-allowed" : "text",
            backgroundColor:"#fffaf5"
          }}
        />
        <OverlayTrigger
          placement="auto-start"
          delay={{ show: 250, hide: 400 }}
          overlay={(propsa) =>
            props.showValidationInfo(propsa, "kommentarÄndringar")
          }
        >
          {showError ? (
            <span
              style={{
                position: "absolute",
                right: 12,
                top: "50%",
                transform: "translateY(-50%)",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <FontAwesomeIcon
                className="exclamationIconStyle"
                icon={faExclamation}
                style={{ color: "rgba(179, 4, 4, 0.8)" }}
              />
            </span>
          ) : (
            <></>
          )}
        </OverlayTrigger>
      </div>
    </div>
  );
};

const renderEditableDeductionCell = (
  item: ProjectReport,
  props: Props,
  isEditing: boolean,
  toggleRowEdit: (item: ProjectReport) => void
): JSX.Element => {
  const deduction = getRowDeduction(item);
  const hasChange = deduction !== 0;

  if (isEditing) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: 140,
          gap: 8,
        }}
      >
        <input
          autoFocus
          style={{
            MozAppearance: "textfield",
            WebkitAppearance: "none",
            appearance: "none",
            width: "100%",
            maxWidth: 120,
            minHeight: 36,
            borderRadius: 10,
            border: "1px solid #ced4da",
            outline: "none",
            padding: "8px 10px",
            textAlign: "right",
            fontWeight: 700,
            color: hasChange ? "#94a3b8" : "#334155",
            backgroundColor: "#fffaf5",
          }}
          type="number"
          value={deduction}
          onChange={(event) => {
            const deductionValue = Number(event.target.value) || 0;
            const reportedSum = item.sumExclMoms - deductionValue;

            props.chageForChangeChange(
              { target: { value: reportedSum } },
              item,
              "sumExclMomsChange"
            );
          }}
        />

        <button
          type="button"
          onClick={() => toggleRowEdit(item)}
          style={{
            border: "none",
            background: "transparent",
            padding: 0,
            margin: 0,
            width: 28,
            height: 28,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "#64748b",
          }}
          aria-label="Stäng redigering"
          title="Stäng"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => toggleRowEdit(item)}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        cursor: "pointer",
        fontWeight: 700,
        color: hasChange ? "#173c53" : "#94a3b8",
      }}
    >
      <span>{formatNumber(deduction)}</span>
      <FontAwesomeIcon
        icon={faPen}
        style={{
          fontSize: 12,
          opacity: 1,
        }}
      />
    </button>
  );
};


const renderMultipleChangesRow = (
  props: Props,
  editingRows: { [key: string]: boolean },
  toggleRowEdit: (item: ProjectReport) => void,
  projectNameSortDirectionByContract: Record<string, "asc" | "desc">,
  toggleProjectNameSortDirection: (contractId: string) => void
): JSX.Element => {
  const contractIds = Object.keys(props.reports);

  return (
    <>
      <StyledAccordion key={2299232323232323232692} title={""} alwaysOpen>
        <div
          ref={props.sectionRef}
          className="datalist"
          style={{
            marginBottom: "30px",
            padding: "8px 0 24px 0"
          }}
        >
          <section style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "0 12px",
              }}
            >
              <div
                style={{
                  height: 1,
                  flex: 1,
                  background: "#dbe3ea",
                }}
              />
            </div>

            {contractIds.map((contractId) => {
              const rows: ProjectReport[] = props.reports[contractId] || [];
              const isConfirmed = !!props.contractConfirmation[contractId];
              const projectNameSortDirection =
                projectNameSortDirectionByContract[contractId] ?? "asc";

              const sortedRows = [...rows].sort((a, b) => {
                const aName = a.projectName ?? "";
                const bName = b.projectName ?? "";

                const comparison = aName.localeCompare(bName, "sv", {
                  numeric: true,
                  sensitivity: "base",
                });

                return projectNameSortDirection === "asc"
                  ? comparison
                  : -comparison;
              });

              return (
                <div
                  key={contractId}
                  className={isConfirmed ? "animate animate-pop" : ""}
                  style={{
                    borderRadius: 4,
                    border: "1px solid rgb(206, 212, 218)",
                    overflow: "hidden",
                    transition: "all 0.2s ease",
                    margin: "0 12px",
                  }}
                >
                  <div
                    style={{
                      padding: "18px 24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 16,
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        minHeight: 32,
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          fontSize: 18,
                          color: "#0f172a",
                        }}
                      >
                        {i18n.t("kickbackProject.contract")} {contractId}
                      </p>

                      {isConfirmed ? (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "4px 10px",
                            borderRadius: 999,
                            background: "rgba(23, 61, 84, 0.08)",
                            color: "#173C53",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          Bekräftad
                        </span>
                      ) : null}
                    </div>

                    <div>{props.checkBoxMenu(contractId)}</div>
                  </div>

                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                        textAlign: "left",
                        fontSize: 14,
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            background: "rgb(246, 242, 237)",
                            color: "rgb(135, 134, 134)",
                            fontWeight: 600,
                          }}
                        >
                          <th style={thLeft}>
                            <button
                              type="button"
                              onClick={() => toggleProjectNameSortDirection(contractId)}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                background: "transparent",
                                border: "none",
                                padding: 0,
                                margin: 0,
                                cursor: "pointer",
                                font: "inherit",
                                color: "inherit",
                              }}
                            >
                              <span>Projektnamn</span>
                              <span style={{ fontSize: 12, lineHeight: 1 }}>
                                {projectNameSortDirection === "asc" ? "▲" : "▼"}
                              </span>
                            </button>
                          </th>
                          
                          <th style={thLeft}>{i18n.t('supplierProjectChangesRowHeaders.invoiceReference')}</th>
                          <th style={thLeft}>{i18n.t('supplierProjectChangesRowHeaders.invoiceLabel')}</th>
                          <th style={thRight}>{i18n.t('supplierProjectChangesRowHeaders.totalExclVat')}</th>
                          <th style={thCenter}>{i18n.t('supplierProjectChangesRowHeaders.expensesExclVat')}</th>
                          <th style={thRight}>{i18n.t('supplierProjectChangesRowHeaders.reportedAmount')}</th>
                          <th style={thRight}>{i18n.t('supplierProjectChangesRowHeaders.totalKickback')}</th>
                          <th style={thLeft}>{i18n.t('supplierProjectChangesRowHeaders.commentChanges')}</th>
                          <th style={thLeft}>{i18n.t('supplierProjectChangesRowHeaders.slutDatum')}</th>
                        </tr>
                      </thead>

                      <tbody>
                        {sortedRows.map((row) => {
                          const deduction = getRowDeduction(row);
                          const hasChange = deduction !== 0;
                          const isEditing = !!editingRows[getRowEditKey(row)];

                          return (
                            <tr
                              key={row.id}
                              style={{
                                transition: "background 0.2s ease",
                              }}
                            >
                              <td style={tdPrimary}>{row.projectName}</td>
                              <td style={td}>{row.invoiceReference}</td>
                              <td style={td}>{row.invoiceIdentifier}</td>
                              <td style={tdRight}>
                                {formatNumber(row.sumExclMoms)}
                              </td>
                              <td style={tdCenter}>
                                {renderEditableDeductionCell(
                                  row,
                                  props,
                                  isEditing,
                                  toggleRowEdit
                                )}
                              </td>
                              <td
                                style={{
                                  ...tdRight,
                                  color: hasChange ? "#94a3b8" : "#0f172a",
                                }}
                              >
                                {formatNumber(row.sumExclMomsChange)}
                              </td>
                              <td style={tdRight}>
                                {ReadFriendlyNumber(row.kickbackSum)}
                              </td>
                              <td style={tdWide}>
                                {renderEditableCommentCell(row, props)}
                              </td>
                              <td style={tdMuted}>
                                {row.projectEndDate == null
                                  ? "—"
                                  : new Date(row.projectEndDate).toLocaleDateString()}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </section>
        </div>
      </StyledAccordion>
    </>
  );
};
const compView = ({
  sectionRef,
  kickbackChangesReport,
  renderKickbackChangesField,
  contractConfirmation,
  checkBoxMenu,
}: Props) => {
  if (typeof kickbackChangesReport !== "undefined") {
    return (
      <StyledAccordion title={""} alwaysOpen>
        <div
          ref={sectionRef}
          className="datalist"
          style={{
            borderTop: "5px solid rgb(143, 169, 186)",
            borderBottom: "2px solid rgb(143, 169, 186)",
            marginBottom: "30px",
          }}
        >
          <table className="datalistTable">
            <thead className="datalistTableHead">
              <tr>
                <th className="datalistTableHeader">Avtal</th>
                <th className="datalistTableHeader">
                  Totalt belopp exkl. moms
                </th>
                <th className="datalistTableHeader">Ev.ändringar</th>
                <th className="datalistTableHeader">Ev.ändringar Kommentar</th>
                <th className="datalistTableHeader">
                  Total kickback rapporterat
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.values(kickbackChangesReport).map((x, i) => {
                return (
                  <tr
                    key={x.contractId}
                    className="datalistTableRow datalistTableRowHover"
                  >
                    <td
                      className="datalistTableCell"
                      style={{ height: "100%" }}
                      aria-disabled
                    >{`${x.contractId} `}</td>
                    <td
                      className="datalistTableCell"
                      style={{ height: "100%" }}
                      aria-disabled
                    >
                      {ReadFriendlyNumber(x.sumTotal)}
                    </td>
                    <td
                      className="datalistTableCell"
                      style={{
                        height: "100%",
                        border: "2px solid #173C53",
                      }}
                    >
                      {renderKickbackChangesField(x, "changedSum")}
                    </td>
                    <td
                      className="datalistTableCell"
                      style={{
                        height: "100%",
                        border: "2px solid #173C53",
                      }}
                    >
                      {renderKickbackChangesField(x, "changesComment")}
                    </td>
                    <td
                      className="datalistTableCell"
                      style={{ height: "100%" }}
                      aria-disabled
                    >
                      {ReadFriendlyNumber(x.totalKickbackReported)}
                    </td>
                    <td
                      className={`datalistTableCell ${
                        contractConfirmation[
                          i18n.t("kickbackProject.invoiceReport")
                        ]
                          ? `animate animate-pop delay-${i}`
                          : ""
                      }`}
                      style={{ height: "100%", width: 30, cursor: "pointer" }}
                    >
                      {checkBoxMenu(x.contractId)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="datalistTableFoot"></tfoot>
          </table>
        </div>
      </StyledAccordion>
    );
  } else {
    return <></>;
  }
};

const thLeft: React.CSSProperties = {
  padding: "14px 24px",
  borderBottom: "1px solid #eef2f6",
  whiteSpace: "nowrap",
};

const thRight: React.CSSProperties = {
  padding: "14px 24px",
  borderBottom: "1px solid #eef2f6",
  textAlign: "right",
  whiteSpace: "nowrap",
};

const thCenter: React.CSSProperties = {
  padding: "14px 24px",
  borderBottom: "1px solid #eef2f6",
  textAlign: "center",
  whiteSpace: "nowrap",
};

const td: React.CSSProperties = {
  padding: "16px 24px",
  borderBottom: "1px solid #f3f6f9",
  color: "#334155",
  verticalAlign: "middle",
};

const tdPrimary: React.CSSProperties = {
  padding: "16px 24px",
  borderBottom: "1px solid #f3f6f9",
  color: "#334155",
  verticalAlign: "middle",
  fontWeight: 600,
};

const tdRight: React.CSSProperties = {
  padding: "16px 24px",
  borderBottom: "1px solid #f3f6f9",
  color: "#475569",
  verticalAlign: "middle",
  textAlign: "right",
  whiteSpace: "nowrap",
  fontWeight: 600,
};

const tdCenter: React.CSSProperties = {
  padding: "16px 24px",
  borderBottom: "1px solid #f3f6f9",
  color: "#475569",
  verticalAlign: "middle",
  textAlign: "center",
};

const tdWide: React.CSSProperties = {
  padding: "16px 24px",
  borderBottom: "1px solid #f3f6f9",
  color: "#475569",
  verticalAlign: "middle",
  minWidth: 220,
};

const tdMuted: React.CSSProperties = {
  padding: "16px 24px",
  borderBottom: "1px solid #f3f6f9",
  color: "#64748b",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};